class PdpBreadcrumb extends HTMLElement {
  static get observedAttributes() {
    return ["breadcrumb", "title"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.foundBreadcrumb = null;
    this.apiUrl = `${window.routes.adminUrl}`; // Storefront API URL
    this.token = `${window.routes.token}`; // Storefront API Token
    this.cacheKey = "RP-shopifyBreadcrumbData"; // Key for localStorage
    this.cacheDuration = 86400 * 1000; // 24 hours in milliseconds
  }

  get breadcrumb() {
    return this.getAttribute("breadcrumb");
  }

  set breadcrumb(value) {
    this.setAttribute("breadcrumb", value);
  }

  get title() {
    return this.getAttribute("title");
  }

  set title(value) {
    this.setAttribute("title", value);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "breadcrumb" && oldValue !== newValue) {
      this.handleAttributeChange(newValue);
    }
  }

  async loadBreadcrumbData() {
    const cachedData = JSON.parse(localStorage.getItem(this.cacheKey));
    const now = Date.now();

    // Check if cached data exists and is not expired
    if (
        cachedData &&
        now - cachedData.timestamp < this.cacheDuration
      ) {
        console.log("cache breadcrumbs");
        return cachedData.data;
      }
      

    // Fetch new data if cache is missing or expired
    const data = await this.fetchData();
    if (data) {
      const result = JSON.parse(data.data.metaobject.fields[0].value);
      // Cache the new data with a timestamp
      localStorage.setItem(
        this.cacheKey,
        JSON.stringify({ data: result, timestamp: now })
      );
      return result;
    }

    return null;
  }

  async fetchData() {
    console.log("fetching new breadcrumbs")
    try {
      const response = await fetch(this.apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": this.token,
        },
        body: JSON.stringify({
          query: `{
                        metaobject(id: "gid://shopify/Metaobject/250952778098") {
                            fields {
                                value
                            }
                        }
                    }`,
        }),
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching data:", error);
      return null;
    }
  }

  async handleAttributeChange(breadcrumbData) {
    const breadcrumbObj = JSON.parse(breadcrumbData);
    if (!breadcrumbObj.breadcrumb) return;

    const cleanedStr = breadcrumbObj.breadcrumb.replace(/\]\s*,\s*\[/g, "],[");
    const wrappedStr = "[" + cleanedStr + "]";
    const parsedArray = JSON.parse(wrappedStr);

    // Load data from cache or API
    const apiResponse = await this.loadBreadcrumbData();
    if (!apiResponse) return;

    this.foundBreadcrumb = this.findBreadcrumb(parsedArray, apiResponse);
    this.render();
  }

  getNameFromName(handle, sort) {
    const found = sort.find(([_, slug]) => slug === handle);
    // console.log("lookding for",handle, "and the display_name is" , found[2])
    return found ? found[2] : handle;
  }

  getNameFromHandle(handle, sort) {
    const found = sort.find(([_, slug]) => slug === handle);
    return found ? found[0] : handle;
  }

  findBreadcrumb(sort, data) {
    const rank = Object.fromEntries(
      sort.map(([_, slug], index) => [slug, sort.length - index])
    );

    const getScore = (category) => category.split(">").length;

    const validCategories = data.filter((category) =>
      category.split(">").every((part) => rank.hasOwnProperty(part))
    );

    if (validCategories.length === 0) return [];

    const scores = Object.fromEntries(
      validCategories.map((category) => [category, getScore(category)])
    );

    const bestCategory = validCategories.sort((a, b) => {
      const scoreDiff = scores[b] - scores[a];
      if (scoreDiff !== 0) return scoreDiff;

      // Extract the lowest sort_order for each category
      const getSortOrder = (category) =>
        Math.min(
          ...category
            .split(">")
            .map(
              (part) => sort.find(([_, slug]) => slug === part)?.[3] || Infinity
            )
        );

      const sortOrderA = getSortOrder(a);
      const sortOrderB = getSortOrder(b);

      if (sortOrderA !== null && sortOrderB !== null) {
        // Use sort_order if both are valid
        return sortOrderA - sortOrderB;
      }

      return data.indexOf(a) - data.indexOf(b);
    })[0];

    return bestCategory.split(">").map((name) => ({
      handle: this.getNameFromHandle(name, sort),
      name: this.getNameFromName(name, sort),
    }));
  }

  formatBreadcrumb(breadcrumb, title) {
    if (!breadcrumb) {
      return `
                <li class="breadcrumb__item">
                    <a class="breadcrumb__link" href="/">Home</a>
                </li>
                <li class="breadcrumb__item">
                    <span class="breadcrumb__link">${title}</span>
                </li>
            `;
    }

    let breadcrumbItems = `
            <li class="breadcrumb__item">
                <a class="breadcrumb__link" href="/">Home</a>
            </li>
        `;

    breadcrumbItems += breadcrumb
      .map((segment) => {
        const href = `/collections/${segment.handle}`;
        return `
                <li class="breadcrumb__item">
                    <a class="breadcrumb__link" href="${href}">${segment.name}</a>
                </li>
            `;
      })
      .join("");

    const additionalItem = `
            <li class="breadcrumb__item">
                <span class="breadcrumb__link">${title}</span>
            </li>
        `;

    return breadcrumbItems + additionalItem;
  }

  render() {
    const formattedContent = this.formatBreadcrumb(
      this.foundBreadcrumb,
      this.title
    );

    this.shadowRoot.innerHTML = `
            <style>
                .breadcrumb__list {
                    list-style: none;
                    padding: 12px 0;
                    margin: 0;
                    display: flex;
                    overflow-x: auto;
                    scroll-snap-type: x mandatory;
                    scrollbar-width: none;
                    scroll-padding: 0 16px;
                }

                .breadcrumb__item {
                    scroll-snap-align: start;
                    flex-shrink: 0;
                }

                .breadcrumb__item:last-child {
                    opacity: 0.7;
                }

                .breadcrumb__item:focus {
                    text-decoration: underline;
                    color: #000;
                }

                .breadcrumb__item:hover {
                    text-decoration: underline;
                    color: #000;
                }

                .breadcrumb__link {
                    transition: opacity .2s ease-in-out;
                    text-decoration: none;
                }

                a {
                    color: inherit;
                    text-decoration: none;
                    opacity: 1;
                }

                .breadcrumb__item + .breadcrumb__item:before {
                    content: "/";
                    margin: 0 4px;
                    opacity: 0.7;
                    float: left;
                }

                .breadcrumb__item:last-of-type span {
                    opacity: 1 !important;
                }
            </style>
            <ol class="breadcrumb__list">
                ${formattedContent}
            </ol>
        `;
  }
}

customElements.define("pdp-breadcrumb", PdpBreadcrumb);